import React, { Component } from 'react';
import styled from 'styled-components';
import { Colors } from 'modules/theme';
import { DeviceTypes, AlertTypes, AlertTrigger } from 'constants/index';
import InputSearch from 'components/InputSearch';
import H5 from 'components/H5';
import Icon from 'components/Icon';
import PaginationButtons from 'components/PaginationButtons';
import Filter, { FilterGroup, FilterContainer } from 'components/Filter';
import Table, { THeader, TableTitle, TRow, TableItem, TBody } from 'components/Table';
import ListEmpty from 'components/ListEmpty';
import moment from 'moment';
import Loader from 'components/Loader';
import PropTypes from 'prop-types';
import { push } from 'modules/history';

const Container = styled.div`
  padding-top: 0;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding-bottom: 20px;
  gap: 16px;
`;

const SearchContainer = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 380px;
  display: flex;
  align-items: center;
  align-self: center;
  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    margin-right: 12px;
  }
`;

const TableContainer = styled.div`
  padding-bottom: 50px;
  margin-bottom: 50px;
`;

const Footer = styled.div`
  position: fixed;
  bottom: 0;
  left: 250px;
  right: 0;
  padding: 0 24px 25px 24px;
  background-color: ${Colors.white};
  box-sizing: border-box;
  @media (max-width: 700px) {
    left: 60px;
  }
`;

const ClickableRow = styled(TRow)`
  cursor: pointer;
  &:hover {
    background: ${Colors.blue}10;
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.04);
  }
  & * {
    cursor: inherit !important;
  }
`;

export default class AlertListView extends Component {
  state = {
    search: '',
    device_type: '',
    status: '',
    brand_id: '',
    account_id: '',
    state: '',
    city: '',
  };

  filterOnChange = event => {
    const { name, value } = event.target;

    if (name === 'brand_id') {
      const { fetchAccounts } = this.props;
      fetchAccounts({ brands: [value] });
    }

    if (name === 'state') {
      const { fetchCities } = this.props;
      fetchCities(value);
    }

    this.setState({ [name]: value }, () => {
      const { fetchAlerts, getPendingCount } = this.props;
      const { search, device_type, status, brand_id, account_id, state, city } = this.state;

      fetchAlerts({ search, device_type, status, brand_id, account_id, state, city });
      getPendingCount();
    });
  };

  searchOnChange = search => {
    this.setState({ search }, () => {
      const { fetchAlerts, getPendingCount } = this.props;
      const { search, device_type, status, brand_id, account_id, state, city } = this.state;

      fetchAlerts({ search, device_type, status, brand_id, account_id, state, city });
      getPendingCount();
    });
  };

  handleAlertClick = item => {
    const { markAlertAsViewed } = this.props;

    // Mark alert as viewed when clicked
    if (item.id) {
      markAlertAsViewed({ alert_id: item.id });
    }

    // Navigate to location
    push(`/locations/${item.location_id}`);
  };

  renderItem = (item, key) => {
    const iconColor =
      item.type === 'warning' ? Colors.red : item.type === 'alert' ? Colors.orange : Colors.green;
    const iconName = item.type === 'success' ? 'check-circle' : 'exclamation-circle';

    const getTriggerLabel = () =>
      AlertTrigger[item.AlertType.device_type].find(el => el.value === item.AlertType.trigger)
        .label;

    const getDeviceType = () => {
      switch (item.AlertType.device_type) {
        case 'water-softener':
          return 'Softener';
        case 'water-filter':
          return 'Water Filter';
        case 'reverse-osmosis':
          return 'Reverse Osmosis';
        default:
          return 'Unknown Device';
      }
    };

    return (
      <ClickableRow key={key} onClick={() => this.handleAlertClick(item)}>
        <TableItem width="25%">
          <H5 style={{ lineHeight: '0px' }}>
            <Icon name={iconName} color={iconColor} customStyle={{ marginRight: '10px' }} />
            {item.title}
          </H5>
        </TableItem>
        <TableItem width="15%">{item.Location.identificator}</TableItem>
        <TableItem width="15%">{getDeviceType()}</TableItem>
        <TableItem width="15%">{getTriggerLabel()}</TableItem>
        <TableItem width="15%">{item.Location.account_name}</TableItem>
        <TableItem width="15%">{moment(item.created_at).format('MM/DD/YYYY hh:mm a')}</TableItem>
      </ClickableRow>
    );
  };

  render() {
    const {
      list,
      loading,
      states,
      cities,
      brands,
      accounts,
      pages,
      currentPage,
      fetchAlerts,
    } = this.props;
    const { search, device_type, status, brand_id, account_id, state, city } = this.state;

    return (
      <Container>
        <Header>
          <SearchContainer>
            <InputSearch onChange={this.searchOnChange} value={search} name="search" />
          </SearchContainer>
        </Header>
        <FilterGroup>
          <FilterContainer>
            <Filter
              title="Device Type:"
              selectedValue={device_type}
              name="device_type"
              handleChange={this.filterOnChange}
              options={[{ label: 'All', value: '' }, ...DeviceTypes]}
            />
          </FilterContainer>
          <FilterContainer>
            <Filter
              title="Status:"
              selectedValue={status}
              name="status"
              handleChange={this.filterOnChange}
              options={[{ label: 'All', value: '' }, ...AlertTypes]}
            />
          </FilterContainer>
          <FilterContainer>
            <Filter
              title="Brand:"
              selectedValue={brand_id}
              name="brand_id"
              handleChange={this.filterOnChange}
              options={brands}
            />
          </FilterContainer>
          <FilterContainer>
            <Filter
              title="Account:"
              selectedValue={account_id}
              name="account_id"
              handleChange={this.filterOnChange}
              options={accounts}
            />
          </FilterContainer>
          <FilterContainer>
            <Filter
              title="State:"
              selectedValue={state}
              name="state"
              handleChange={this.filterOnChange}
              options={states}
            />
          </FilterContainer>
          <FilterContainer>
            <Filter
              title="City:"
              selectedValue={city}
              name="city"
              handleChange={this.filterOnChange}
              options={cities}
            />
          </FilterContainer>
        </FilterGroup>

        <TableContainer>
          <Table>
            <THeader>
              <TableTitle width="25%">Location</TableTitle>
              <TableTitle width="15%">Location Id</TableTitle>
              <TableTitle width="15%">Device</TableTitle>
              <TableTitle width="15%">Trigger</TableTitle>
              <TableTitle width="15%">Account</TableTitle>
              <TableTitle width="15%">Date</TableTitle>
            </THeader>
            <TBody>
              {loading ? (
                <Loader size={100} block />
              ) : (
                list.map((item, key) => this.renderItem(item, key))
              )}
            </TBody>
          </Table>
          {(!list || !list.length) && !loading && (
            <ListEmpty message="There are currently no alerts" />
          )}
        </TableContainer>
        <Footer>
          <PaginationButtons
            currentPage={currentPage}
            changePage={page =>
              fetchAlerts({ page, search, device_type, status, brand_id, account_id, state, city })
            }
            totalPages={pages}
          />
        </Footer>
      </Container>
    );
  }
}

AlertListView.propTypes = {
  accounts: PropTypes.array,
  brands: PropTypes.array,
  cities: PropTypes.array,
  currentPage: PropTypes.number,
  fetchAccounts: PropTypes.func,
  fetchAlerts: PropTypes.func,
  fetchCities: PropTypes.func,
  getPendingCount: PropTypes.func,
  list: PropTypes.array,
  loading: PropTypes.bool,
  markAlertAsViewed: PropTypes.func,
  pages: PropTypes.number,
  states: PropTypes.array,
};
